import type { ChipTone } from '../../components/aa';
import type { OrbitAsk, OrbitRoomPerson, OrbitRoomPurpose } from '../../types/aai';

/*
 * Words the Room uses, in one place. A screen names a state; it never invents
 * a label of its own.
 */

/* The nouns of the Room. Musawir is choosing a new word for "dossier"; when it
 * arrives it changes here and nowhere else. */
export const WORDS = {
  room: 'Orbit Room',
  journey: 'Journey',
  company: 'Company',
  dossier: 'Dossier',
  dossiers: 'Dossiers',
  collected: 'Collected',
  toCollect: 'To collect',
  opportunities: 'Opportunities',
  overview: 'Overview',
  addCard: 'Add a card',
  card: 'card',
  cards: 'cards',
};

export const PURPOSES: { id: OrbitRoomPurpose; label: string }[] = [
  { id: 'business', label: 'Business' },
  { id: 'department', label: 'Department' },
  { id: 'problem', label: 'Problem' },
];

/* Who a row waits on, in as few words as the roster allows: the person's name
 * when Orbit has one, else the part of the address before the @, which reads
 * as a name and keeps a row one line. The full address stays in the drawer. */
export const shortName = (email: string | null | undefined, people?: OrbitRoomPerson[]): string => {
  if (!email) return 'nobody yet';
  const known = (people || []).find((p) => p.email === email.toLowerCase());
  if (known && known.name) return known.name;
  const local = email.split('@')[0];
  if (!local) return email;
  return local.split(/[._-]/).filter(Boolean).map((part) => part[0].toUpperCase() + part.slice(1)).join(' ');
};

export const statusChip = (ask: OrbitAsk, people?: OrbitRoomPerson[]): { label: string; tone: ChipTone } => {
  if (!ask.responsible_email) return { label: 'needs someone to answer', tone: 'warn' };
  if (ask.status === 'delegated') return { label: `waiting on ${shortName(ask.responsible_email, people)}`, tone: 'warn' };
  if (ask.status === 'asked') return { label: 'asked', tone: 'run' };
  if (ask.later) return { label: 'later', tone: 'neutral' };
  return { label: 'open', tone: 'run' };
};

export const originLine = (ask: OrbitAsk, people?: OrbitRoomPerson[]): string => {
  if (ask.origin === 'orbit_gap') return 'Orbit, from the file';
  if (ask.origin === 'orbit_reading') return 'Orbit, from what it read';
  if (ask.origin === 'partner') return `Added by ${shortName(ask.created_by, people)}, Solution Partner`;
  return `Added by ${shortName(ask.created_by, people)}`;
};

export const handedLine = (ask: OrbitAsk, people?: OrbitRoomPerson[]): string | null => {
  if (ask.chain.length === 0) return null;
  const names = ask.chain.map((c) => shortName(c.from, people));
  return `Handed on by ${names.join(', ')}`;
};

export const responsibleLine = (ask: OrbitAsk, me: string, people?: OrbitRoomPerson[]): string => {
  if (!ask.responsible_email) return 'the department head, once there is one';
  if (ask.responsible_email === me.toLowerCase()) return 'you';
  return shortName(ask.responsible_email, people);
};

export const plural = (n: number, one: string, many: string): string => {
  if (n === 1) return `${n} ${one}`;
  return `${n} ${many}`;
};

export const countLine = (counts: { known: number; confirm: number; ask: number; waiting: number }): string => {
  const parts: string[] = [];
  if (counts.known > 0) parts.push(`${counts.known} collected`);
  if (counts.confirm > 0) parts.push(`${counts.confirm} to confirm`);
  if (counts.ask > 0) parts.push(`${counts.ask} to ask`);
  if (counts.waiting > 0) parts.push(`${counts.waiting} waiting`);
  if (parts.length === 0) return 'nothing read yet';
  return parts.join(' · ');
};

export const whenLabel = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const scopeName = (slug: string): string => slug.split('-').map((part) => {
  if (!part) return part;
  return part[0].toUpperCase() + part.slice(1);
}).join(' ');



export const bucketOfDossier = (kind: string, bucket: string): string => {
  if (kind === 'department') return 'dept';
  return bucket;
};
